import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { ModalProps } from '@chakra-ui/react';

import { Box, FormControl, InputGroup } from '../../component-primitives';
import { useBreakpointValue } from '../../hooks/break-points-value';
import { useDisclosure } from '../../hooks/use-disclosure';
import { ActionButton } from '../action-button';
import { CloseIcon } from '../icons/ui-icons/close-icon';
import { Input } from '../input';
import { InputRightElement } from '../input-right-element';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '../modal';
import { Option, Placeholder } from './components';
import { useSelect } from './hooks/use-select';
import { SelectProps } from './types';

export const FilterSelect = (props: SelectProps) => {
  const {
    placeholder,
    options = [],
    onChange,
    defaultValue = '',
    name,
    id,
    optionTemplate,
    disabled,
    onBlur,
    'data-test-id': dataTestId,
  } = props as SelectProps;
  const { isOpen, onOpen, onClose } = useDisclosure();

  const modalSize = useBreakpointValue<ModalProps['size']>({
    base: 'full',
    md: 'md',
  });
  const modalHeight = useBreakpointValue({ base: '100vh', md: '112' });
  const padding = useBreakpointValue({
    base: [15, 18, 5, 5],
    md: [10, 10, 12, 12],
  });
  const [pt, pb, pl, pr] = padding || [];
  const contentHeight = useBreakpointValue({ base: '100vh', md: '72' });

  const {
    selectOption,
    selectedOptions,
    filteredOptions,
    inputRef,
    isSelected,
    setQuery,
    clear,
    handleKeyDown,
    focusedIndex,
    onFocus,
  } = useSelect({
    options,
    onChange,
    isOpen,
    defaultValue,
  });

  useLayoutEffect(() => {
    if (Object.keys(selectedOptions).length > 0) {
      onClose();
      if (onChange) {
        onChange(selectedOptions?.value);
      }
    }
  }, [selectedOptions]);

  const scrollableContainerRef = useRef(null);

  useEffect(() => {
    if (focusedIndex !== null && scrollableContainerRef.current) {
      const focusedItem = (scrollableContainerRef.current as HTMLInputElement)
        .children[focusedIndex];
      if (focusedItem) {
        focusedItem.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      }
    }
  }, [focusedIndex]);

  return (
    <>
      <Placeholder
        onOpen={onOpen}
        selectedOptions={selectedOptions}
        disabled={disabled}
        placeholder={placeholder}
        optionTemplate={optionTemplate}
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={modalSize}
        motionPreset="slideInBottom"
      >
        <ModalOverlay variant="semi-transparent" />
        <ModalContent
          minHeight={modalHeight}
          pt={pt}
          pb={pb}
          pl={pl}
          pr={pr}
          data-test-id={dataTestId}
        >
          <ModalCloseButton />
          <ModalBody overflow="hidden" p="0">
            <FormControl>
              <InputGroup>
                <Input
                  name={name}
                  id={id}
                  autoFocus
                  ref={inputRef}
                  placeholder={placeholder}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    clear(true);
                  }}
                  onKeyDown={handleKeyDown}
                  onFocus={onFocus}
                  ignoreContext
                  onBlur={onBlur}
                />
                <InputRightElement>
                  {Object.keys(selectedOptions)?.length ? (
                    <ActionButton
                      pt="1"
                      pb="1"
                      height="auto"
                      paddingInlineStart="1"
                      paddingInlineEnd="1"
                      variant="secondary"
                      size="small"
                      rightIcon={<CloseIcon size="extra-small" />}
                      onClick={() => clear()}
                      aria-label="Close"
                    />
                  ) : null}
                </InputRightElement>
              </InputGroup>
              <Box
                bg="white"
                maxHeight={contentHeight}
                overflowY="scroll"
                position="absolute"
                zIndex={999}
                left={0}
                right={0}
                onScroll={(e) => {
                  e.stopPropagation();
                }}
                ref={scrollableContainerRef}
              >
                {filteredOptions.length ? (
                  <div>
                    {filteredOptions.map((option, index) => (
                      <Option
                        key={String(option.value)}
                        value={option.value}
                        selected={isSelected(option)}
                        onClick={() => selectOption(option)}
                        isFocused={focusedIndex === index}
                      >
                        {optionTemplate(option)}
                      </Option>
                    ))}
                  </div>
                ) : (
                  <Option disabled>No results</Option>
                )}
              </Box>
            </FormControl>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
